// Wave 19 — CAPI dead-letter queue triage.
//
// Cursor-paginated view over `/api/admin/capi-dlq`. Workspace
// admins (and owners) see every CAPI conversion that exhausted
// the in-process retry budget and got parked in `capi_dead_letter`.
// They can replay the entry against its forwarder (which deletes
// the row on success or bumps the attempt counter on failure) or
// discard it permanently. Every action is audit-logged server-side.
import ConfirmDialog from './ConfirmDialog';

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { RefreshCw, Repeat, Trash2 } from 'lucide-react';

const EMPTY_FILTERS = {
  provider: '',
  forwarder_id: '',
  conversion_id: '',
  since: '',
  until: '',
};

function formatTimestamp(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function AdminCapiDlq() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [busyId, setBusyId] = useState(null);

  const fetchPage = async (opts = {}) => {
    const { append = false, cursor = null } = opts;
    setLoading(true);
    if (!append) setItems([]);
    setError(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) params[key] = value.trim();
      });
      if (cursor) params.cursor = cursor;
      const r = await axios.get(`${API}/admin/capi-dlq`, { params });
      const incoming = Array.isArray(r.data?.items) ? r.data.items : [];
      setItems((prev) => (append ? [...prev, ...incoming] : incoming));
      setNextCursor(r.data?.next_cursor || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || t('Failed to load CAPI DLQ');
      setError(detail);
      if (!append) setItems([]);
      if (e?.response?.status !== 403) {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const apply = () => fetchPage();
  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setNextCursor(null);
    setTimeout(() => fetchPage(), 0);
  };
  const loadMore = () => {
    if (nextCursor) fetchPage({ append: true, cursor: nextCursor });
  };
  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const replay = async (entry) => {
    if (!entry?.id) return;
    setBusyId(entry.id);
    try {
      const r = await axios.post(`${API}/admin/capi-dlq/${entry.id}/replay`);
      if (r.data?.ok) {
        toast.success(t('CAPI replay succeeded'));
        // The backend has deleted the row on success — drop it locally.
        setItems((prev) => prev.filter((x) => x.id !== entry.id));
      } else {
        toast.error(
          r.data?.error
            ? `${t('Replay failed:')} ${r.data.error}`
            : t('Replay failed'),
        );
        // Bump attempts in-place so the operator can see the new state
        // without a full refresh.
        setItems((prev) =>
          prev.map((x) =>
            x.id === entry.id
              ? {
                  ...x,
                  attempts: r.data?.attempt || x.attempts + 1,
                  last_error: r.data?.error || x.last_error,
                }
              : x,
          ),
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Replay failed'));
    } finally {
      setBusyId(null);
    }
  };

  const [discardTarget, setDiscardTarget] = useState(null);

  const discard = async (entry) => {
    if (!entry?.id) return;
    setDiscardTarget(null);
    setBusyId(entry.id);
    try {
      await axios.delete(`${API}/admin/capi-dlq/${entry.id}`);
      toast.success(t('DLQ entry discarded'));
      setItems((prev) => prev.filter((x) => x.id !== entry.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Discard failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="xp-page">
      <Card data-testid="admin-capi-dlq-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle>{t('CAPI dead-letter queue')}</CardTitle>
            <CardDescription>
              {t(
                'Conversions that exhausted the retry budget. Replay re-attempts the forwarder; discard drops the entry permanently.',
              )}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={apply}
            disabled={loading}
            data-testid="capi-dlq-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('Refresh')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div>
              <Label htmlFor="dlq-filter-provider">{t('Provider')}</Label>
              <Input
                id="dlq-filter-provider"
                placeholder="facebook"
                value={filters.provider}
                onChange={(e) => setFilter('provider', e.target.value)}
                data-testid="dlq-filter-provider"
              />
            </div>
            <div>
              <Label htmlFor="dlq-filter-forwarder">
                {t('Forwarder ID')}
              </Label>
              <Input
                id="dlq-filter-forwarder"
                value={filters.forwarder_id}
                onChange={(e) => setFilter('forwarder_id', e.target.value)}
                data-testid="dlq-filter-forwarder"
              />
            </div>
            <div>
              <Label htmlFor="dlq-filter-conv">{t('Conversion ID')}</Label>
              <Input
                id="dlq-filter-conv"
                value={filters.conversion_id}
                onChange={(e) => setFilter('conversion_id', e.target.value)}
                data-testid="dlq-filter-conv"
              />
            </div>
            <div>
              <Label htmlFor="dlq-filter-since">{t('Since (ISO)')}</Label>
              <Input
                id="dlq-filter-since"
                placeholder="2025-01-01T00:00:00Z"
                value={filters.since}
                onChange={(e) => setFilter('since', e.target.value)}
                data-testid="dlq-filter-since"
              />
            </div>
            <div>
              <Label htmlFor="dlq-filter-until">{t('Until (ISO)')}</Label>
              <Input
                id="dlq-filter-until"
                placeholder="2025-12-31T23:59:59Z"
                value={filters.until}
                onChange={(e) => setFilter('until', e.target.value)}
                data-testid="dlq-filter-until"
              />
            </div>
          </div>
          <div className="flex gap-2 mb-4">
            <Button onClick={apply} disabled={loading} data-testid="dlq-apply">
              {t('Apply filters')}
            </Button>
            <Button
              variant="outline"
              onClick={reset}
              disabled={loading}
              data-testid="dlq-reset"
            >
              {t('Reset')}
            </Button>
          </div>

          {error && (
            <p className="text-sm text-destructive mb-3" data-testid="dlq-error">
              {error}
            </p>
          )}

          {!loading && items.length === 0 && !error ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="dlq-empty"
            >
              {t('No DLQ entries match these filters.')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Time')}</TableHead>
                  <TableHead>{t('Provider')}</TableHead>
                  <TableHead>{t('Forwarder')}</TableHead>
                  <TableHead>{t('Conversion')}</TableHead>
                  <TableHead className="text-right">{t('Attempts')}</TableHead>
                  <TableHead>{t('Last error')}</TableHead>
                  <TableHead className="text-right">{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((entry) => (
                  <TableRow key={entry.id} data-testid={`dlq-row-${entry.id}`}>
                    <TableCell>{formatTimestamp(entry.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.provider || '—'}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {entry.forwarder_id || '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {entry.conversion_id || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.attempts ?? 0}
                    </TableCell>
                    <TableCell
                      className="text-xs text-destructive max-w-xs truncate"
                      title={entry.last_error || ''}
                    >
                      {entry.last_error || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => replay(entry)}
                          disabled={busyId === entry.id}
                          aria-label={t('Replay')}
                          title={t('Replay')}
                          data-testid={`dlq-replay-${entry.id}`}
                        >
                          <Repeat className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDiscardTarget(entry)}
                          disabled={busyId === entry.id}
                          aria-label={t('Discard')}
                          title={t('Discard')}
                          data-testid={`dlq-discard-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {nextCursor && (
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loading}
                data-testid="dlq-load-more"
              >
                {t('Load more')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!discardTarget}
        onOpenChange={(open) => { if (!open) setDiscardTarget(null); }}
        title={t('Discard DLQ entry')}
        description={t('Permanently discard this dead-letter entry? This cannot be undone.')}
        confirmLabel={t('Discard')}
        onConfirm={() => discard(discardTarget)}
      />
    </div>
  );
}
